/**
 * Keep React rendering on a page someone else is rewriting.
 *
 * "Translate this page" (Chrome, Safari, Edge) replaces the text nodes React
 * rendered with <font> elements, or moves them inside one. React still holds
 * the originals, so its next insertBefore or removeChild against one of them
 * throws NotFoundError and the nearest error boundary takes the tool down.
 * Every analysis-panel crash in production was this, all of them for writers
 * whose browsers were not set to English. Extensions that rewrite text do the
 * same.
 *
 * Only calls the DOM would otherwise reject are changed, and only when the node
 * was detached or merely wrapped inside the parent. A node that lives somewhere
 * else on the page still throws, so genuine mistakes stay loud.
 */
export type DomRecovery = "insertBefore" | "removeChild";

let installed: (() => void) | null = null;

/**
 * Patch `Node.prototype` (once; later calls return the first uninstaller).
 * `onRecover` hears about the first recovery of each kind, after the current
 * DOM work has finished.
 */
export function installDomMutationGuard(onRecover?: (operation: DomRecovery) => void): () => void {
  if (installed) return installed;

  const proto = Node.prototype;
  const { insertBefore, removeChild } = proto;
  const reported = new Set<DomRecovery>();

  const recovered = (operation: DomRecovery) => {
    if (!onRecover || reported.has(operation)) return;
    reported.add(operation);
    setTimeout(() => {
      try {
        onRecover(operation);
      } catch {
        // Reporting must never interrupt rendering.
      }
    }, 0);
  };

  proto.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && child.parentNode !== this) {
      if (!child.parentNode) {
        recovered("insertBefore");
        return insertBefore.call(this, node, null) as T;
      }
      const wrapper = childContaining(this, child);
      if (wrapper) {
        recovered("insertBefore");
        return insertBefore.call(this, node, wrapper) as T;
      }
    }
    return insertBefore.call(this, node, child) as T;
  };

  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (!child.parentNode) {
        recovered("removeChild");
        return child;
      }
      if (this.contains(child)) {
        recovered("removeChild");
        return removeChild.call(child.parentNode, child) as T;
      }
    }
    return removeChild.call(this, child) as T;
  };

  installed = () => {
    proto.insertBefore = insertBefore;
    proto.removeChild = removeChild;
    installed = null;
  };
  return installed;
}

/** The direct child of `parent` that holds `node`, or null when it lives elsewhere. */
function childContaining(parent: Node, node: Node): Node | null {
  let current: Node | null = node;
  while (current && current.parentNode !== parent) current = current.parentNode;
  return current;
}
